import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { t } from '@/lib/translations';

export const TrialWelcomeDialog: React.FC = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { isTrialing, trialEndsAt, isLoading } = useSubscription();

  const [noticeSeen, setNoticeSeen] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const markedRef = useRef(false);

  // Load the profile flag once per user.
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setNoticeSeen(null);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('trial_notice_seen_at')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error('TrialWelcomeDialog: failed to load profile flag', error);
        setNoticeSeen(true); // fail closed – do not show
        return;
      }
      setNoticeSeen(Boolean(data?.trial_notice_seen_at));
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const shouldShow = !!user && !isLoading && isTrialing && noticeSeen === false;

  useEffect(() => {
    if (shouldShow) setOpen(true);
  }, [shouldShow]);

  const markSeen = async () => {
    setOpen(false);
    if (markedRef.current || !user?.id) return;
    markedRef.current = true;
    setNoticeSeen(true);
    const { error } = await supabase
      .from('profiles')
      .update({ trial_notice_seen_at: new Date().toISOString() })
      .eq('user_id', user.id);
    if (error) console.error('TrialWelcomeDialog: failed to mark notice as seen', error);
  };

  if (!shouldShow && !open) return null;

  const formattedDate = trialEndsAt
    ? new Date(trialEndsAt).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '';

  const text = t('auth', 'trialDialogText', language).replace('{date}', formattedDate);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) void markSeen(); }}>
      <DialogContent
        className="max-w-[420px] rounded-2xl border p-6 sm:p-7"
        style={{ background: '#121a25', borderColor: '#243140' }}
      >
        <DialogHeader>
          <DialogTitle
            className="text-left text-xl"
            style={{ fontFamily: 'Archivo, sans-serif', color: '#e9eff6' }}
          >
            {t('auth', 'trialDialogTitle', language)}
          </DialogTitle>
          <DialogDescription className="text-left text-sm leading-relaxed" style={{ color: '#93a1b1' }}>
            {text}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => void markSeen()}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ background: '#34d27a', color: '#0b1119' }}
          >
            {t('auth', 'trialDialogCta', language)}
          </button>
          <Link
            to="/pro"
            onClick={() => void markSeen()}
            className="text-xs underline underline-offset-4"
            style={{ color: '#34d27a' }}
          >
            {t('auth', 'trialDialogProLink', language)}
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};
